<template>
	<VDropdown
		:disabled="isTooltipDisabled"
		:distance="8"
		:triggers="['hover']"
		placement="right-top"
	>
		<SuiAvatar
			:aria-label="$t('user_assign_extra_label')"
			:size="avatarSize"
			:initials="`+${hiddenCount}`"
			:color="Constants.Colors.INACTIVE_COLOR"
			show-border
		/>

		<template #popper>
			<div class="user-assign-extra-tooltip">
				<div
					v-for="assignee in hiddenAssignees"
					:key="assignee.data.id"
					class="user-assign-extra-tooltip__item"
				>
					<SuiAvatar
						size="medium"
						:user-id="
							assignee.type === 'user'
								? assignee.data.id
								: undefined
						"
						:initials="assignee.data.initials ?? ''"
						:color="assignee.data.hslColor ?? ''"
						:url="
							assignee.type === 'user'
								? assignee.data.profilePicture?.mini
								: undefined
						"
						:aria-label="
							assignee.type === 'user'
								? assignee.data.firstName
								: assignee.data.name
						"
						:variant="
							assignee.type === 'role' ? 'dashed' : undefined
						"
						show-border
					/>

					<span class="user-assign-extra-tooltip__name">
						{{
							assignee.type === 'user'
								? assignee.data.firstName
								: assignee.data.name
						}}
					</span>
					<span
						v-if="getAllocatedTime(assignee.data.id, assignee.type)"
						class="user-assign-extra-tooltip__time"
					>
						{{
							formatAllocatedTime(assignee.data.id, assignee.type)
						}}
					</span>
				</div>
			</div>
		</template>
	</VDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Dropdown as VDropdown } from 'floating-vue';
import { SuiAvatar } from '@developers/scoro-ui';
import { secondsToHoursAndMinutes } from '@/utils/time';
import { Constants } from '@/constants';
import type { UserAssignSelectUserFragment } from '@/generated/graphql';
import type { UserAssignSelectRole, UserAssignAvatarSize } from '../../types';
import type { TaskAssigneeAllocatedDuration } from '@/features/Task/components/TaskUsers/types';

export interface UserAssignSelectTriggerExtraCountProps {
	hiddenCount: number;
	hiddenUsers: UserAssignSelectUserFragment[];
	hiddenRoles: UserAssignSelectRole[];
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	avatarSize: UserAssignAvatarSize;
	isUserSelectOpen: boolean;
	isTooltipEnabled?: boolean;
}

const {
	isTooltipEnabled = false,
	allocatedTimes = undefined,
	hiddenUsers,
	hiddenRoles,
	isUserSelectOpen,
} = defineProps<UserAssignSelectTriggerExtraCountProps>();

type AssigneeItem =
	| { type: 'user'; data: UserAssignSelectUserFragment }
	| { type: 'role'; data: UserAssignSelectRole };

const isTooltipDisabled = computed(() => isUserSelectOpen || !isTooltipEnabled);

const hiddenAssignees = computed((): AssigneeItem[] => {
	const roles: AssigneeItem[] = hiddenRoles.map((role) => ({
		type: 'role',
		data: role,
	}));
	const users: AssigneeItem[] = hiddenUsers.map((user) => ({
		type: 'user',
		data: user,
	}));

	return [...roles, ...users];
});

const getAllocatedTime = (
	id: string,
	type: 'user' | 'role'
): number | undefined => {
	if (type === 'user') {
		return allocatedTimes?.users.get(id);
	}
	return allocatedTimes?.roles.get(id);
};

const formatAllocatedTime = (id: string, type: 'user' | 'role'): string => {
	const seconds = getAllocatedTime(id, type);
	if (seconds === undefined) {
		return '';
	}
	return secondsToHoursAndMinutes(seconds);
};
</script>

<style scoped lang="scss">
.user-assign-extra-tooltip {
	display: flex;
	flex-wrap: wrap;
	gap: 2rem;
	padding: 2rem 3rem;
	max-width: min(75rem, 90vw);
	max-height: 90vh;

	&__item {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		align-items: center;
		width: 5rem;
	}

	&__name {
		font-size: 1.375rem;
		color: $neutral-90;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		width: 100%;
		max-width: 100%;
		text-align: center;
	}

	&__time {
		font-size: 1.25rem;
		color: $neutral-70;
	}
}
</style>
